import React, { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import {
  Truck, Bug, Fence, Package, FileText, AlertTriangle,
  ScrollText, ArrowRight, TrendingUp, BarChart3
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { useAuthStore, authFetch } from "@/lib/auth";
import { StatusBadge } from "@/components/StatusBadge";
import { PageHeader } from "@/components/PageHeader";
import {
  PieChart, Pie, Cell, ResponsiveContainer, Tooltip as RechartsTooltip,
  BarChart, Bar, XAxis, YAxis, CartesianGrid
} from "recharts";
import { format, parseISO } from "date-fns";
import { cn } from "@/lib/utils";
import type { DashboardSummary, Animal, InventoryItem, LogEntry } from "@shared/types";
const logTypeColors: Record<string, string> = {
  maintenance: "text-sky-muted bg-sky-muted/10 dark:text-sky-muted-dark dark:bg-sky-muted-dark/15",
  pasture: "text-forest bg-forest/10 dark:text-forest-dark dark:bg-forest-dark/15",
  vet: "text-moss bg-moss/10 dark:text-moss-dark dark:bg-moss-dark/15",
  "animal-care": "text-burnt-orange bg-burnt-orange/10 dark:text-burnt-orange-dark dark:bg-burnt-orange-dark/15",
};
// Brand palette for Recharts cells (Pie/Bar). Order matches role hierarchy:
// Forest → Moss → Olive → Clay → Burnt Orange → Sky Muted.
// Spec ref: HollerHub Brand Guidelines v1.1, §04 Color Palette.
const PIE_COLORS = ["#2D4A3E", "#4A7C59", "#6B7C4A", "#B5603E", "#C4622D", "#4A90A4"];
const BAR_COLORS: Record<string, string> = {
  maintenance: "#4A90A4",   // Sky Muted — info / mechanical service
  pasture:     "#2D4A3E",   // Forest — land use
  vet:         "#4A7C59",   // Moss — health / success
  "animal-care": "#C4622D", // Burnt Orange — daily wellness CTA family
};
interface ChartData {
  animals: Animal[];
  inventory: InventoryItem[];
  logs: LogEntry[];
}
function SpeciesChart({ animals }: { animals: Animal[] }) {
  const data = useMemo(() => {
    const counts: Record<string, number> = {};
    for (const a of animals) {
      const s = a.species || "other";
      counts[s] = (counts[s] || 0) + 1;
    }
    return Object.entries(counts)
      .map(([name, value]) => ({ name: name.charAt(0).toUpperCase() + name.slice(1), value }))
      .sort((a, b) => b.value - a.value);
  }, [animals]);
  if (data.length === 0) {
    return (
      <div className="flex items-center justify-center h-48 text-sm text-muted-foreground">
        No animal data
      </div>
    );
  }
  return (
    <div className="flex items-center gap-4">
      <ResponsiveContainer width="50%" height={160}>
        <PieChart>
          <Pie
            data={data}
            cx="50%"
            cy="50%"
            innerRadius={35}
            outerRadius={65}
            paddingAngle={3}
            dataKey="value"
            stroke="none"
          >
            {data.map((_, index) => (
              <Cell key={`cell-${index}`} fill={PIE_COLORS[index % PIE_COLORS.length]} />
            ))}
          </Pie>
          <RechartsTooltip
            contentStyle={{
              backgroundColor: "hsl(var(--card))",
              border: "1px solid hsl(var(--border))",
              borderRadius: "8px",
              fontSize: "12px",
            }}
          />
        </PieChart>
      </ResponsiveContainer>
      <div className="flex-1 space-y-1.5">
        {data.map((entry, i) => (
          <div key={entry.name} className="flex items-center justify-between text-sm">
            <div className="flex items-center gap-2">
              <div
                className="h-2.5 w-2.5 rounded-full shrink-0"
                style={{ backgroundColor: PIE_COLORS[i % PIE_COLORS.length] }}
              />
              <span className="text-muted-foreground">{entry.name}</span>
            </div>
            <span className="font-medium text-foreground">{entry.value}</span>
          </div>
        ))}
      </div>
    </div>
  );
}
function LogTypeChart({ logs }: { logs: LogEntry[] }) {
  const data = useMemo(() => {
    const counts: Record<string, number> = {};
    for (const l of logs) {
      const t = l.log_type || "other";
      counts[t] = (counts[t] || 0) + 1;
    }
    return Object.entries(counts)
      .map(([name, value]) => ({
        name: name.replace(/-/g, " ").replace(/\b\w/g, (c) => c.toUpperCase()),
        value,
        key: name,
      }))
      .sort((a, b) => b.value - a.value);
  }, [logs]);
  if (data.length === 0) {
    return (
      <div className="flex items-center justify-center h-48 text-sm text-muted-foreground">
        No log data
      </div>
    );
  }
  return (
    <ResponsiveContainer width="100%" height={180}>
      <BarChart data={data} margin={{ top: 5, right: 5, left: -20, bottom: 5 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
        <XAxis
          dataKey="name"
          tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }}
          axisLine={{ stroke: "hsl(var(--border))" }}
          tickLine={false}
        />
        <YAxis
          tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }}
          axisLine={false}
          tickLine={false}
          allowDecimals={false}
        />
        <RechartsTooltip
          contentStyle={{
            backgroundColor: "hsl(var(--card))",
            border: "1px solid hsl(var(--border))",
            borderRadius: "8px",
            fontSize: "12px",
          }}
        />
        <Bar dataKey="value" radius={[4, 4, 0, 0]}>
          {data.map((entry) => (
            <Cell
              key={entry.key}
              fill={BAR_COLORS[entry.key] || "#8B6914"}
            />
          ))}
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  );
}
function InventoryStatusChart({ inventory }: { inventory: InventoryItem[] }) {
  const { ok, low } = useMemo(() => {
    let okCount = 0;
    let lowCount = 0;
    for (const item of inventory) {
      if (item.quantity < item.min_quantity) {
        lowCount++;
      } else {
        okCount++;
      }
    }
    return { ok: okCount, low: lowCount };
  }, [inventory]);
  const total = ok + low;
  if (total === 0) {
    return (
      <div className="flex items-center justify-center h-20 text-sm text-muted-foreground">
        No inventory data
      </div>
    );
  }
  const okPct = Math.round((ok / total) * 100);
  const lowPct = 100 - okPct;
  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        <div className="flex-1 h-3 rounded-full bg-muted overflow-hidden">
          <div
            className="h-full rounded-full transition-all duration-500"
            style={{
              width: `${okPct}%`,
              background: "linear-gradient(90deg, #4A7C59, #7B9E6B)",
            }}
          />
        </div>
      </div>
      <div className="flex items-center justify-between text-sm">
        <div className="flex items-center gap-2">
          <div className="h-2.5 w-2.5 rounded-full bg-ranch-green" />
          <span className="text-muted-foreground">Stocked ({ok})</span>
          <span className="font-medium text-foreground">{okPct}%</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="h-2.5 w-2.5 rounded-full bg-destructive" />
          <span className="text-muted-foreground">Low ({low})</span>
          <span className="font-medium text-foreground">{lowPct}%</span>
        </div>
      </div>
    </div>
  );
}
export function HomePage() {
  const ranchName = useAuthStore((s) => s.ranch?.name ?? "My Ranch");
  const userName = useAuthStore((s) => s.user?.name ?? "");
  const [data, setData] = useState<DashboardSummary | null>(null);
  const [chartData, setChartData] = useState<ChartData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  useEffect(() => {
    let cancelled = false;
    async function load() {
      try {
        const [summary, animalsRes, inventoryRes, logsRes] = await Promise.all([
          authFetch<DashboardSummary>("/api/dashboard/summary"),
          authFetch<{ items: Animal[] }>("/api/animals").catch(() => ({ items: [] })),
          authFetch<{ items: InventoryItem[] }>("/api/inventory").catch(() => ({ items: [] })),
          authFetch<{ items: LogEntry[] }>("/api/logs").catch(() => ({ items: [] })),
        ]);
        if (!cancelled) {
          setData(summary);
          setChartData({
            animals: animalsRes.items ?? [],
            inventory: inventoryRes.items ?? [],
            logs: logsRes.items ?? [],
          });
        }
      } catch (err: unknown) {
        const message = err instanceof Error ? err.message : String(err);
        if (!cancelled) setError(message || "Failed to load dashboard");
        console.error("[Dashboard]", message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => {
      cancelled = true;
    };
  }, []);
  const metricCards = data
    ? [
        {
          label: "Vehicles",
          value: data.vehicleCount,
          icon: Truck,
          href: "/vehicles",
          color: "text-ranch-brown",
          bgAccent: "bg-ranch-brown/5",
        },
        {
          label: "Animals",
          value: data.animalCount,
          icon: Bug,
          href: "/animals",
          color: "text-ranch-green",
          bgAccent: "bg-ranch-green/5",
        },
        {
          label: "Pasture Acres",
          value: data.pastureAcreage,
          icon: Fence,
          href: "/pastures",
          color: "text-ranch-gold",
          bgAccent: "bg-ranch-gold/5",
        },
        {
          label: "Low Stock Items",
          value: data.lowStockCount,
          icon: AlertTriangle,
          href: "/inventory",
          color:
            data.lowStockCount > 0 ? "text-destructive" : "text-muted-foreground",
          bgAccent:
            data.lowStockCount > 0 ? "bg-destructive/5" : "bg-muted/50",
        },
      ]
    : [];
  const greeting = useMemo(() => {
    const hour = new Date().getHours();
    if (hour < 12) return "Good morning";
    if (hour < 17) return "Good afternoon";
    return "Good evening";
  }, []);
  return (
    <>
      <div className="mb-6">
        <p className="text-sm text-muted-foreground mb-1">
          {greeting}, {userName || "Rancher"}
        </p>
        <h1 className="font-display text-3xl text-charcoal dark:text-charcoal-dark">{ranchName} Dashboard</h1>
      </div>
      {error && (
        <div className="mb-6 p-4 rounded-lg bg-destructive/10 text-destructive text-sm">
          {error}
        </div>
      )}
      {/* Metric Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        {loading
          ? Array.from({ length: 4 }).map((_, i) => (
              <Card key={i} className="p-5">
                <Skeleton className="h-4 w-20 mb-3" />
                <Skeleton className="h-8 w-16" />
              </Card>
            ))
          : metricCards.map((card) => (
              <Link key={card.label} to={card.href}>
                <Card
                  className={cn(
                    "p-5 hover:shadow-md transition-all duration-200 cursor-pointer group border",
                    card.bgAccent
                  )}
                >
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-sm text-muted-foreground font-medium">
                      {card.label}
                    </span>
                    <div
                      className={cn(
                        "flex h-8 w-8 items-center justify-center rounded-lg bg-background shadow-sm",
                        "group-hover:scale-110 transition-transform duration-200"
                      )}
                    >
                      <card.icon className={cn("h-4 w-4", card.color)} />
                    </div>
                  </div>
                  <p className="font-mono text-3xl font-bold text-foreground tracking-tight">
                    {card.value}
                  </p>
                </Card>
              </Link>
            ))}
      </div>
      {/* Charts Row */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-8">
        {/* Species Distribution */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium flex items-center gap-2 text-muted-foreground">
              <TrendingUp className="h-4 w-4" />
              Herd Composition
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loading || !chartData ? (
              <div className="space-y-3">
                <Skeleton className="h-40 w-full rounded" />
              </div>
            ) : (
              <SpeciesChart animals={chartData.animals} />
            )}
          </CardContent>
        </Card>
        {/* Log Activity by Type */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium flex items-center gap-2 text-muted-foreground">
              <BarChart3 className="h-4 w-4" />
              Activity by Type
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loading || !chartData ? (
              <Skeleton className="h-44 w-full rounded" />
            ) : (
              <LogTypeChart logs={chartData.logs} />
            )}
          </CardContent>
        </Card>
        {/* Inventory Health */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium flex items-center gap-2 text-muted-foreground">
              <Package className="h-4 w-4" />
              Inventory Health
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loading || !chartData ? (
              <Skeleton className="h-16 w-full rounded" />
            ) : (
              <InventoryStatusChart inventory={chartData.inventory} />
            )}
            {data && !loading && (
              <div className="mt-4 pt-3 border-t space-y-2">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">Total Items</span>
                  <span className="font-medium">{data.inventoryCount}</span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">Need Reorder</span>
                  <span
                    className={cn(
                      "font-medium",
                      data.lowStockCount > 0
                        ? "text-destructive"
                        : "text-ranch-green"
                    )}
                  >
                    {data.lowStockCount}
                  </span>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
      {/* Two column layout */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Recent Activity */}
        <div className="lg:col-span-2">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-3">
              <CardTitle className="text-lg">Recent Activity</CardTitle>
              <Link to="/logs">
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-muted-foreground hover:text-foreground"
                >
                  View All <ArrowRight className="ml-1 h-4 w-4" />
                </Button>
              </Link>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="space-y-3">
                  {Array.from({ length: 4 }).map((_, i) => (
                    <div key={i} className="flex gap-3">
                      <Skeleton className="h-8 w-8 rounded-full" />
                      <div className="flex-1 space-y-1">
                        <Skeleton className="h-4 w-3/4" />
                        <Skeleton className="h-3 w-1/2" />
                      </div>
                    </div>
                  ))}
                </div>
              ) : data?.recentActivity?.events && data.recentActivity.events.length > 0 ? (
                <div className="space-y-4">
                  {data.recentActivity.events.map((log) => (
                    <div
                      key={log.id}
                      className="flex items-start gap-3 group"
                    >
                      <div
                        className={cn(
                          "flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-xs font-medium transition-transform group-hover:scale-110",
                          logTypeColors[log.log_type] ||
                            "text-muted-foreground bg-surface-raised dark:bg-surface-raised-dark"
                        )}
                      >
                        <ScrollText className="h-4 w-4" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-foreground truncate">
                          {log.entity_name}
                        </p>
                        <p className="text-sm text-muted-foreground line-clamp-1">
                          {log.description}
                        </p>
                        <p className="text-2xs text-muted-foreground mt-0.5">
                          {log.date
                            ? format(
                                parseISO(log.date + "T00:00:00"),
                                "MMM d, yyyy"
                              )
                            : ""}
                        </p>
                      </div>
                      <StatusBadge status={log.log_type} />
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-muted-foreground py-4 text-center">
                  No recent activity
                </p>
              )}
            </CardContent>
          </Card>
        </div>
        {/* Quick Actions + Document Summary */}
        <div className="space-y-4">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-lg">Quick Actions</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              <Link to="/vehicles" className="block">
                <Button
                  variant="outline"
                  className="w-full justify-start gap-2 hover:border-ranch-brown/40 transition-colors"
                >
                  <Truck className="h-4 w-4 text-ranch-brown" />
                  Manage Vehicles
                </Button>
              </Link>
              <Link to="/animals" className="block">
                <Button
                  variant="outline"
                  className="w-full justify-start gap-2 hover:border-ranch-green/40 transition-colors"
                >
                  <Bug className="h-4 w-4 text-ranch-green" />
                  Manage Animals
                </Button>
              </Link>
              <Link to="/documents/upload" className="block">
                <Button
                  variant="outline"
                  className="w-full justify-start gap-2 hover:border-ranch-gold/40 transition-colors"
                >
                  <FileText className="h-4 w-4 text-ranch-gold" />
                  Upload Document
                </Button>
              </Link>
              <Link to="/inventory" className="block">
                <Button
                  variant="outline"
                  className="w-full justify-start gap-2 hover:border-muted-foreground/40 transition-colors"
                >
                  <Package className="h-4 w-4 text-muted-foreground" />
                  Check Inventory
                </Button>
              </Link>
            </CardContent>
          </Card>
          {data && (
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-lg flex items-center gap-2">
                  <FileText className="h-4 w-4 text-ranch-gold" />
                  Documents
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  <div className="flex items-center justify-between py-1.5">
                    <span className="text-sm text-muted-foreground">Total</span>
                    <span className="text-sm font-semibold tabular-nums">
                      {data.documentCount}
                    </span>
                  </div>
                  <div className="flex items-center justify-between py-1.5">
                    <span className="text-sm text-muted-foreground">
                      Pending Review
                    </span>
                    <span
                      className={cn(
                        "text-sm font-semibold tabular-nums",
                        data.pendingDocCount > 0
                          ? "text-sky-muted dark:text-sky-muted-dark"
                          : "text-muted-foreground"
                      )}
                    >
                      {data.pendingDocCount}
                    </span>
                  </div>
                  <div className="flex items-center justify-between py-1.5">
                    <span className="text-sm text-muted-foreground">
                      Total Logs
                    </span>
                    <span className="text-sm font-semibold tabular-nums">
                      {data.recentActivity?.events?.length ?? 0}
                    </span>
                  </div>
                </div>
                <div className="mt-3 pt-3 border-t">
                  <Link to="/documents">
                    <Button
                      variant="ghost"
                      size="sm"
                      className="w-full text-muted-foreground hover:text-foreground"
                    >
                      View All Documents
                      <ArrowRight className="ml-1 h-4 w-4" />
                    </Button>
                  </Link>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </>
  );
}